// frontend/src/components/common/AppDateRangePicker.jsx
//
// Two linked AppDatePickers for a date range. Built-in cross-constraints
// (start <= end) plus optional absolute bounds (`minAllowed`, `maxAllowed`)
// so the picker can't produce an invalid request. Callers always pass and
// receive 'YYYY-MM-DD' strings.
//
// Contract: the consumer owns the values. The component does NOT
// self-correct if a value falls outside the allowed range (e.g. bounds
// shifted under a previously-valid saved value). Instead it signals the
// violation via the standard MUI TextField error state + helperText —
// the user sees a red field with a specific message and picks again.
// This is deliberate: writing back from a child effect races parent-side
// persistence (see AppMonthRangeSelect for the same rationale).
//
// For month-range selection (bounded, finite list of months with data),
// use `AppMonthRangeSelect` instead — a pair of `Select` dropdowns is the
// cleaner primitive for that shape.
//
// Both fields carry their own label, so the pair never sits in a FieldLine
// (whose text would name it a second time).

import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import AppDatePicker from './AppDatePicker';
import { asDate, dayInRange } from './dateStringUtils';
import { useFieldLineLabel } from './fieldLineLabel';

function AppDateRangePicker({
  startValue,
  onStartChange,
  endValue,
  onEndChange,
  minAllowed,
  maxAllowed,
  startLabel,
  endLabel,
  startRequired = false,
  endRequired = false,
  disabled = false,
  // When true, the end field stays disabled until a start date is chosen —
  // the range must be built start-first. Opt-in so independent start/end
  // consumers keep the default (both always active).
  endRequiresStart = false,
  width = 160,
  gap = 2,
  sx,
}) {
  const { t } = useTranslation();
  const inFieldLine = useFieldLineLabel() != null;
  if (startLabel == null || endLabel == null || inFieldLine) {
    throw new Error('AppDateRangePicker: give both fields a label, and keep the pair out of a FieldLine');
  }

  const startDate = asDate(startValue);
  const endDate = asDate(endValue);
  const floor = asDate(minAllowed);
  const ceiling = asDate(maxAllowed);

  const startMin = floor;
  const startMax = endDate || ceiling;
  const endMin = startDate || floor;
  const endMax = ceiling;

  // A value handed in outside its bounds (bounds arrived after a stored
  // value, or the other end moved under it) is flagged with a message saying
  // so; AppDatePicker itself only keeps a typed day outside them from the
  // caller.
  const outOfRange = (value, date, min, max) => !!value && !dayInRange(date, min, max);
  const errorProps = (hasError) => (hasError
    ? { slotProps: { textField: { error: true, helperText: t('common.dateOutOfRange') } } }
    : {});

  const endGatedByStart = endRequiresStart && !startDate;

  return (
    <Box sx={{ display: 'flex', gap, alignItems: 'flex-start', flexWrap: 'wrap', ...sx }}>
      <AppDatePicker
        label={startLabel}
        value={startValue}
        onChange={onStartChange}
        minDate={startMin}
        maxDate={startMax}
        required={startRequired}
        disabled={disabled}
        width={width}
        {...errorProps(outOfRange(startValue, startDate, startMin, startMax))}
      />
      <AppDatePicker
        label={endLabel}
        value={endValue}
        onChange={onEndChange}
        minDate={endMin}
        maxDate={endMax}
        required={endRequired}
        disabled={disabled || endGatedByStart}
        width={width}
        {...errorProps(outOfRange(endValue, endDate, endMin, endMax))}
      />
    </Box>
  );
}

export default AppDateRangePicker;
